import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getAuthToken } from '../services/api';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api/v1';

interface LeaderboardEntry {
  userId: string;
  name: string;
  team_id: string;
  total_points: number;
  wallet_balance: number;
  admissions_count: number;
}

const TEAM_COLORS: Record<string, string> = {
  blue: '#2563eb',
  green: '#16a34a',
  red: '#dc2626',
  yellow: '#ca8a04',
  orange: '#ea580c'
};

const IndividualLeaderboard: React.FC = () => {
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLeaderboard();
  }, []);

  const fetchLeaderboard = async () => {
    try {
      const token = await getAuthToken();
      const response = await axios.get(`${API_URL}/admissions-game/leaderboard?limit=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setLeaderboard(response.data.data);
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="p-6">Loading leaderboard...</div>;
  }

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-6">Individual Leaderboard</h2>
      
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rank</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Team</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Admissions</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Points</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Wallet (₹)</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {leaderboard.map((entry, index) => (
              <tr key={entry.userId} className={index < 3 ? 'bg-yellow-50' : ''}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    {index === 0 && <span className="text-2xl mr-2">🥇</span>}
                    {index === 1 && <span className="text-2xl mr-2">🥈</span>}
                    {index === 2 && <span className="text-2xl mr-2">🥉</span>}
                    <span className="font-medium">{index + 1}</span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">{entry.name}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span 
                    className="px-2 py-1 text-xs font-semibold rounded uppercase text-white"
                    style={{ backgroundColor: TEAM_COLORS[entry.team_id] || '#6b7280' }}
                  >
                    {entry.team_id || 'N/A'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  {entry.admissions_count}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  {entry.total_points}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium text-green-600">
                  ₹{entry.wallet_balance.toLocaleString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default IndividualLeaderboard;
